// THE `scout_doable` PAYLOAD (#7569, epic #6552, plan 101 `01-scout-handoff.md`) — the
// scout's hand-off to the Planner: the ask is doable, and here is every plan-call input the
// box holds that the mothership cannot re-derive from the scout task's row and run events.
// #6764's Planner lane composes the plan prompt from it (`buildScoutUserPrompt` is what the
// box builds from the same inputs today, `apps/runner/src/modes/scout/brain/run.ts`).
//
// READER-FIRST: #6765's box is the first writer
// (`apps/runner/src/modes/scout/brain/handoff.ts`), and only on an assignment carrying
// `planner_handoff`, which nothing sets until #6764's server half (R5) — so every reader
// learns the shape first.
//
// NOT CARRIED, on purpose:
//   - the envelope's task text (title, problem, acceptance criteria, files, forbidden, repo),
//     the account and `require_approval`: #6764 reads them from the scout task's row (the
//     #1943 fence), so a box cannot restate them;
//   - the conversation digest and the carry-over. `scout_doable` is a `status` run event,
//     and status payloads are never pruned (`status` is not in `RAW_LOG_EVENT_KINDS`,
//     `apps/worker/src/cron/prune-run-event-payloads.ts`). Both texts deliberately borrow the
//     72 h raw-log window (`../fleet-task-wire.ts`, `conversation_digest` / `carry_over`), so
//     a copy here would make that window permanent. #6764 renders both mothership-side from
//     their sources and owns screening them.
//
// ONE DEFINITION of each wire shape: `RepoQaAnswerSchema` is what `@developerz/agent`'s
// `RepoQaAnswer` is derived from, and `RepoEnvNameSchema` what the runner's `RepoEnvName`
// is, so a field added on either side is a type error at the other side's use sites.

import { z } from 'zod';
import { SecretEnvironmentSchema } from '../project-ingress';
import { GroundedFactSchema } from '../task-envelope/grounding';

/**
 * The repo-QA grounding answer (`answerRepoQuestion`, `@developerz/agent` `repo-qa.ts`):
 * a grounded answer with its citations, or a refusal with its reason.
 */
export const RepoQaAnswerSchema = z.discriminatedUnion('refused', [
  z.object({ refused: z.literal(false), answer: z.string(), citations: z.array(z.string()) }),
  z.object({ refused: z.literal(true), reason: z.string() }),
]);

/** One stored secret's NAME and the environment it is filed under. Never a value. */
export const RepoEnvNameSchema = z.object({
  /** The env-var NAME (already POSIX-gated by `RepoSecretNameSchema` at seal time). */
  name: z.string(),
  environment: SecretEnvironmentSchema,
});

export const ScoutDoablePayloadSchema = z.object({
  event: z.literal('scout_doable'),
  /** Why the scout judged the ask doable: the decision call's reason. */
  reason: z.string(),
  /**
   * The human guidance gathered while scouting (`runScoutTurnLoop`), kept whole: trimmed,
   * blanks dropped, never cut, as the scout's hand-off retains it
   * (`apps/runner/src/modes/scout/brain/handoff.ts`). May be empty.
   */
  guidance: z.array(z.string()),
  /** The box's `ctx.ask(...)` grounding answer. */
  grounded: RepoQaAnswerSchema,
  /** The survey's evidence paths (`surveyEvidencePaths`, always a set). May be empty. */
  anchors: z.array(z.string()),
  /** The survey's anchored facts (`surveyGrounding`). May be empty. */
  grounding: z.array(GroundedFactSchema),
  /** The survey brief, UNFITTED (`briefOf(survey)`). Absent: no survey ran. */
  survey_brief: z.string().optional(),
  /** The repo's KB digest as the box resolved it (`scoutKbDigest`). Absent: none. */
  kb_digest: z.string().optional(),
  /**
   * The stored secrets' names the box was handed (`ctx.vaultNames`). ABSENCE PRESERVED:
   * absent (no vault seam) and `[]` (an empty vault) are different facts, and the prompt
   * says different things for each.
   */
  vault_names: z.array(RepoEnvNameSchema).optional(),
});
export type ScoutDoablePayload = z.infer<typeof ScoutDoablePayloadSchema>;
